import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  BarChart3, MonitorDot, Trophy, History,
  Receipt, Library, ShieldCheck, Settings2, X, MoreHorizontal, Bot,
} from "lucide-react";

//  Custom SVG Icons 

function IconDashboard({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" strokeWidth="1.5" stroke="currentColor" className={className}>
      <rect x="1" y="1" width="6" height="6" rx="0.5" />
      <rect x="9" y="1" width="6" height="6" rx="0.5" />
      <rect x="1" y="9" width="6" height="6" rx="0.5" />
      <rect x="9" y="9" width="6" height="6" rx="0.5" />
    </svg>
  );
}

function IconJournal({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" strokeWidth="1.5" stroke="currentColor" className={className}>
      <rect x="2" y="1" width="10" height="14" rx="0.5" />
      <line x1="5" y1="5" x2="9" y2="5" />
      <line x1="5" y1="8" x2="9" y2="8" />
      <line x1="5" y1="11" x2="8" y2="11" />
    </svg>
  );
}

function IconAgents({ className }: { className?: string }) {
  return <Bot className={className} strokeWidth={1.5} />;
}

function IconEdgeScore({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" strokeWidth="1.5" stroke="currentColor" className={className}>
      <path d="M3 12 A6 6 0 0 1 13 12" strokeLinecap="round" />
      <line x1="3.5" y1="11" x2="4.5" y2="9.5" strokeLinecap="round" />
      <line x1="8" y1="6" x2="8" y2="7.5" strokeLinecap="round" />
      <line x1="12.5" y1="11" x2="11.5" y2="9.5" strokeLinecap="round" />
      <line x1="8" y1="12" x2="10.5" y2="8.5" strokeLinecap="round" strokeWidth="1.5" />
      <circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconTerminal({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" strokeWidth="1.5" stroke="currentColor" className={className}>
      <rect x="1" y="2" width="14" height="12" rx="0.5" />
      <polyline points="4,6 7,8 4,10" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="8" y1="10" x2="12" y2="10" strokeLinecap="round" />
    </svg>
  );
}

//  Nav items (primary 5) 

const PRIMARY_NAV = [
  { icon: IconDashboard, label: "Dashboard", path: "/",           exact: true  },
  { icon: IconJournal,   label: "Journal",   path: "/journal",    exact: false },
  { icon: IconAgents,    label: "Agents",    path: "/agents?agent=journal", exact: false },
  { icon: IconEdgeScore, label: "Edge",      path: "/edge-score", exact: false },
  { icon: Settings2,     label: "Settings",  path: "/settings",   exact: false },
];

//  More drawer items 

const MORE_NAV = [
  { icon: Settings2,       label: "Settings",         path: "/settings"             },
  { icon: BarChart3,       label: "Analytics",        path: "/analytics"            },
  { icon: MonitorDot,      label: "Command Center",   path: "/command-center"       },
  { icon: Trophy,          label: "Leaderboard",      path: "/leaderboard"          },
  { icon: ShieldCheck,     label: "Prop Challenge",   path: "/prop-challenge"       },
  { icon: History,         label: "Trade Replay",     path: "/trade-replay"         },
  { icon: Receipt,         label: "Tax / MTD",        path: "/tax"                  },
  // Strategy Library temporarily hidden for launch (see LEGAL_AUDIT.md).
  // { icon: Library,         label: "Strategy Library", path: "/strategy-library"     },
];

//  More drawer 

function MoreDrawer({ onClose }: { onClose: () => void }) {
  return (
    <div className="lg:hidden fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet from bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-black border-t border-white/[0.08] pb-safe">
        {/* Handle + header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <span className="font-mono text-[10px] tracking-[0.15em] text-white/40 uppercase">
            All Pages
          </span>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4" style={{ color: "rgba(255,255,255,0.4)" }} strokeWidth={1.5} />
          </button>
        </div>

        {/* Grid of links */}
        <div className="grid grid-cols-4 gap-0 p-2">
          {MORE_NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1.5 py-4 px-2 transition-colors",
                  isActive ? "text-[#1d9e75]" : "text-white/40 hover:text-white/70"
                )
              }
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[9px] font-medium uppercase tracking-[0.06em] text-center leading-tight">
                {item.label}
              </span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

//  Component 

export function BottomNav() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isMoreActive = MORE_NAV.some((item) => location.pathname.startsWith(item.path));

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-black border-t border-white/[0.06]">
        <div className="flex items-center justify-around h-[4.25rem] px-1 pb-safe">
          {PRIMARY_NAV.map((item) => {
            const itemPathname = item.path.split("?")[0];
            const isActive = item.exact
              ? location.pathname === itemPathname
              : location.pathname.startsWith(itemPathname);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 min-h-[44px] py-2 transition-colors",
                  isActive ? "text-[#1d9e75]" : "text-white/35"
                )}
              >
                <item.icon className="w-[20px] h-[20px]" />
                <span className="text-[9px] font-medium uppercase tracking-[0.06em]">
                  {item.label}
                </span>
              </NavLink>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center gap-1 flex-1 min-h-[44px] py-2 transition-colors",
              isMoreActive ? "text-[#1d9e75]" : "text-white/35"
            )}
          >
            <MoreHorizontal className="w-[20px] h-[20px]" strokeWidth={1.5} />
            <span className="text-[9px] font-medium uppercase tracking-[0.06em]">More</span>
          </button>
        </div>
      </nav>

      {moreOpen && <MoreDrawer onClose={() => setMoreOpen(false)} />}
    </>
  );
}
